import { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);

  const chatEndRef = useRef(null);
  const isMobile = window.innerWidth <= 768;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  async function ask() {
    if (!question.trim()) return;

    setMessages(prev => [...prev, { role: "user", content: question }]);
    setLoading(true);
    setQuestion("");

    try {
      const res = await fetch(`${API_BASE_URL}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question }),
      });

      const data = await res.json();

      setMessages(prev => [
        ...prev,
        { role: "assistant", content: data.answer },
      ]);
    } catch {
      setMessages(prev => [
        ...prev,
        { role: "assistant", content: "Something went wrong ❌" },
      ]);
    }

    setLoading(false);
  }

  const styles = {
    chatButton: {
      position: "fixed",
      bottom: "24px",
      right: "24px",
      width: "56px",
      height: "56px",
      borderRadius: "50%",
      border: "none",
      background: "#10a37f",
      color: "#fff",
      fontSize: "24px",
      cursor: "pointer",
      zIndex: 1000,
    },

    chatWindow: {
      position: "fixed",
      bottom: "90px",
      right: "24px",
      width: isMobile ? "90%" : "360px",
      height: "520px",
      background: "#fff",
      borderRadius: "16px",
      boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
      overflow: "hidden",
      zIndex: 1000,
      display: "flex",
      flexDirection: "column",
    },

    header: {
      padding: "12px 16px",
      borderBottom: "1px solid #eee",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      fontWeight: "500",
    },

    chatArea: {
      flex: 1,
      padding: isMobile ? "12px" : "16px",
      overflowY: "auto",
    },

    messageRow: {
      display: "flex",
      marginBottom: "12px",
    },

    bubble: {
      padding: "12px 16px",
      borderRadius: "12px",
      maxWidth: "90%",
      fontSize: "15px",
      lineHeight: "1.6",
      wordBreak: "break-word",
    },

    inputWrapper: {
      padding: "10px",
      borderTop: "1px solid #eee",
      background: "#fff",
    },

    inputBox: {
      display: "flex",
      alignItems: "center",
      border: "1px solid #ddd",
      borderRadius: "16px",
      padding: "8px 12px",
    },

    input: {
      flex: 1,
      border: "none",
      outline: "none",
      fontSize: "15px",
    },

    sendBtn: {
      border: "none",
      background: "#000",
      color: "#fff",
      borderRadius: "50%",
      width: "36px",
      height: "36px",
      cursor: "pointer",
    },
  };

  return (
    <>
      {/* Floating Button */}
      <button
        style={styles.chatButton}
        onClick={() => setOpen(!open)}
      >
        💬
      </button>

      {/* Chat Window */}
      {open && (
        <div style={styles.chatWindow}>
          {/* Header */}
          <div style={styles.header}>
            <span>AI Assistant</span>
            <button
              onClick={() => setOpen(false)}
              style={{
                border: "none",
                background: "transparent",
                fontSize: "18px",
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>

          {/* Messages */}
          <div style={styles.chatArea}>
            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  ...styles.messageRow,
                  justifyContent:
                    msg.role === "user" ? "flex-end" : "flex-start",
                }}
              >
                <div
                  style={{
                    ...styles.bubble,
                    background:
                      msg.role === "user" ? "#10a37f" : "#f3f3f3",
                    color: msg.role === "user" ? "#fff" : "#000",
                  }}
                >
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                </div>
              </div>
            ))}

            {loading && (
              <div style={styles.messageRow}>
                <div
                  style={{ ...styles.bubble, background: "#f3f3f3" }}
                >
                  Typing…
                </div>
              </div>
            )}

            <div ref={chatEndRef} />
          </div>

          {/* Input */}
          <div style={styles.inputWrapper}>
            <div style={styles.inputBox}>
              <input
                style={styles.input}
                value={question}
                onChange={e => setQuestion(e.target.value)}
                placeholder="Ask anything"
                onKeyDown={e => e.key === "Enter" && ask()}
              />
              <button style={styles.sendBtn} onClick={ask}>
                ➤
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}